// CustomerManagement.js - 사이드바에 사용자 이름 추가 버전
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Menu, X, Settings, Plus, Bell, MessageCircle, Download, ChevronUp, List, LayoutGrid, Users, Image, Type, Map as MapIcon, Building2 } from 'lucide-react';
import CustomerOverview from './components/CustomerOverview';
import CustomerForm from './components/CustomerForm';
import Notice from './components/Notice';
import ReportGenerator from './components/ReportGenerator';
import AssistantModal from './components/AssistantModal';
// getKoreanTime은 이 파일에서 사용하지 않음
import HelpModal from './modals/HelpModal';
import KakaoMapModal from './modals/KakaoMapModal';
import ImportLegacyModal from './modals/ImportLegacyModal';
import BrowserModal from './modals/BrowserModal';
import ShareBrowserModal from './components/ShareBrowserModal';
import ConfirmModal from './modals/ConfirmModal';
import DeleteModal from './modals/DeleteModal';
import MoveModal from './modals/MoveModal';
import BulkMoveModal from './modals/BulkMoveModal';
import DataMigrationAlert from './components/DataMigrationAlert';
import TermsOfServiceModal from './modals/TermsOfServiceModal';
import PrivacyPolicyModal from './modals/PrivacyPolicyModal';
import SettingsModal from './modals/SettingsModal';
import BulkCustomerInputModal from './modals/BulkCustomerInputModal';
import { useCustomerData } from './hooks/useCustomerData';
import { useActionLog } from './hooks/useActionLog';
import { useVisitTracker } from './hooks/useVisitTracker';
// usePushNotification 훅은 설정 모달에서만 사용
import { generateDeviceId, isKakaoInAppBrowser } from './utils/deviceUtils';
import { CUSTOMER_STAGES as DEFAULT_STAGES } from './utils/customerStages';
import { getCustomerStages } from './utils/customerStages';
import { db, setupMessageListener, getDeviceLinkInfo, DEVICE_STATUS } from './firebase';
import { idbGetCustomersByStage, idbPutCustomer } from './utils/idb';
import ArcadeModal from './components/ArcadeModal';
import EuphoriaModal from './components/EuphoriaModal';
import WelcomeModal from './components/WelcomeModal';
import NextActionModal from './components/NextActionModal';
import TrialCustomerRegistrationModal from './components/TrialCustomerRegistrationModal';
import { idbGetAllHistories } from './utils/idb';
import { exportCustomersCsvWithSections } from './utils/exportUtils';
import { sidebarVariants, floatingButtonVariants, buttonVariants } from './utils/animationUtils';
import { calculateRemainingDays } from './utils/trialUtils';

const CustomerManagement = ({ appStatus, trialInfo }) => {
    // 커스텀 훅을 통한 상태 관리
    const {
        customers,
        setCustomers,
        currentStep,
        setCurrentStep,
        loading: customersLoading,
        error: customersError,
        addCustomer,
        updateCustomer,
        deleteCustomer: deleteCustomerFromIdb,
        reload: reloadCustomers
    } = useCustomerData();

    // eslint-disable-next-line no-unused-vars
    const navigate = null; // Removed useNavigate but keeping structure if needed or just remove completely

    const [showArcade, setShowArcade] = useState(false);
    const [aiBadgeCount, setAiBadgeCount] = useState(0);
    const [showEaster, setShowEaster] = useState(false);

    // 기기 링크 정보 상태
    const [deviceLinkInfo, setDeviceLinkInfo] = useState(null);

    // AI 비서 배지 카운트 계산: 오늘 생일 + 오늘 Follow Up
    useEffect(() => {
        const calc = async () => {
            try {
                // 페이지네이션과 관계없이 IndexedDB에서 직접 모든 고객 조회
                const { idbGetAllCustomers } = await import('./utils/idb');
                const allCustomers = await idbGetAllCustomers();

                // KST 기준으로 오늘 날짜 계산 (시간대 문제 방지)
                const now = new Date();
                const kstTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
                const yyyy = String(kstTime.getFullYear());
                const mm = String(kstTime.getMonth() + 1).padStart(2, '0');
                const dd = String(kstTime.getDate()).padStart(2, '0');
                const todayStr = `${yyyy}-${mm}-${dd}`;

                // 생일 계산 (모든 고객 대상)
                const todayMonth = kstTime.getMonth() + 1;
                const todayDay = kstTime.getDate();
                const isBirthdayToday = (birthdateValue) => {
                    if (!birthdateValue) return false;

                    let month, day;

                    // 문자열 형식인 경우
                    if (typeof birthdateValue === 'string') {
                        // 앞뒤 공백 제거
                        const trimmed = birthdateValue.trim();
                        const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
                        if (match) {
                            month = parseInt(match[2], 10);
                            day = parseInt(match[3], 10);
                        } else {
                            // 다른 형식의 문자열인 경우 Date 객체로 파싱 시도
                            const birth = new Date(trimmed);
                            if (isNaN(birth.getTime())) return false;
                            month = birth.getMonth() + 1;
                            day = birth.getDate();
                        }
                    } else {
                        // Date 객체나 타임스탬프인 경우
                        const birth = new Date(birthdateValue);
                        if (isNaN(birth.getTime())) return false;
                        month = birth.getMonth() + 1;
                        day = birth.getDate();
                    }

                    return month === todayMonth && day === todayDay;
                };
                const birthdayCount = allCustomers.filter(c => {
                    if (!c?.birthdate) return false;
                    return isBirthdayToday(c.birthdate);
                }).length;

                // 할일 (히스토리의 followUp)
                let todoCount = 0;
                try {
                    const histories = await idbGetAllHistories();
                    if (Array.isArray(histories)) {
                        todoCount = histories.filter(h => {
                            const f = h?.followUp;
                            if (!f) return false;
                            if (typeof f === 'object' && f?.date) return typeof f.date === 'string' ? f.date.startsWith(todayStr) : false;
                            if (typeof f === 'string') return f.startsWith(todayStr);
                            return false;
                        }).length;
                    }
                } catch (_) { /* ignore */ }

                const totalCount = birthdayCount + todoCount;
                setAiBadgeCount(totalCount);

                // PWA 앱 배지 카운트 설정
                if ('setAppBadge' in navigator) {
                    try {
                        if (totalCount > 0) {
                            await navigator.setAppBadge(totalCount);
                        } else {
                            await navigator.clearAppBadge();
                        }
                    } catch (error) {
                        console.warn('PWA 앱 배지 설정 실패:', error);
                    }
                }
            } catch (_) {
                setAiBadgeCount(0);
                // PWA 앱 배지 초기화
                if ('setAppBadge' in navigator) {
                    try {
                        await navigator.clearAppBadge();
                    } catch (error) {
                        console.warn('PWA 앱 배지 초기화 실패:', error);
                    }
                }
            }
        };
        calc();
    }, [customers]);

    const { sendActionLog } = useActionLog();

    // 방문 추적 훅 추가
    const deviceId = generateDeviceId();
    const [businessType, setBusinessType] = useState(() => {
        try { return localStorage.getItem('businessType') || 'insurance'; } catch (_) { return 'insurance'; }
    });
    const CUSTOMER_STAGES = safeGetStages(businessType);
    useVisitTracker(deviceId);

    // 마이그레이션 상태 확인
    useEffect(() => {
        const checkMigrationStatus = async () => {
            try {
                const { idbGetSetting } = await import('./utils/idb');
                const customersMigrated = await idbGetSetting('customers_migrated');
                const historiesMigrated = await idbGetSetting('histories_migrated');
                const hasLocalStorageCustomers = !!localStorage.getItem('customerData');
                const hasLocalStorageHistories = !!localStorage.getItem('customerHistory');

                // 마이그레이션이 필요한 경우 알림 표시
                const needsMigration = (hasLocalStorageCustomers || hasLocalStorageHistories) &&
                    (!customersMigrated || !historiesMigrated);

                if (needsMigration) {
                    console.warn('⚠️ 데이터 마이그레이션이 필요합니다.');
                    setShowMigrationAlert(true);
                }
            } catch (e) {
                console.error('마이그레이션 상태 확인 실패:', e);
            }
        };

        // 앱 로드 후 마이그레이션 상태 확인
        const timer = setTimeout(checkMigrationStatus, 2000);
        return () => clearTimeout(timer);
    }, []);

    // 체험 사용자 고객 등록 유도 모달 표시 로직
    useEffect(() => {
        // 고객 데이터가 로드되지 않았으면 실행하지 않음
        if (customersLoading) return;

        // 체험 상태 확인
        const isTrialUser =
            appStatus === 'trial' ||
            appStatus === 'expired' ||
            (appStatus === 'pending' && trialInfo?.status === 'trial');

        if (!isTrialUser) return;

        // 고객 수 확인
        const totalCustomers = Object.values(customers).reduce(
            (sum, stageCustomers) => sum + (stageCustomers?.length || 0),
            0
        );

        // 고객이 있으면 모달 표시하지 않음
        if (totalCustomers > 0) return;

        // 하루에 한 번만 표시하는 로직
        const getTodayDateString = () => {
            const now = new Date();
            const kstTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
            const yyyy = String(kstTime.getFullYear());
            const mm = String(kstTime.getMonth() + 1).padStart(2, '0');
            const dd = String(kstTime.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        const today = getTodayDateString();
        const lastShownDate = localStorage.getItem('trialRegistrationModalLastShown');

        // 오늘 이미 표시했으면 표시하지 않음
        if (lastShownDate === today) return;

        // 모달 표시
        setShowTrialRegistrationModal(true);
    }, [customersLoading, customers, appStatus, trialInfo]);

    // 온보딩 고객 생성 이벤트 처리: 고객 생성 + 활동 로그 기록 + NextAction 오픈
    useEffect(() => {
        const handler = async (e) => {
            try {
                const detail = (e && e.detail) || {};
                const name = (detail.name || '').trim();
                const phone = (detail.phone || '').trim();
                const memo = (detail.memo || '').trim();
                if (!name || !phone) return;

                // 체험 종료 상태일 때 누적 고객 등록수 제한 체크
                if (appStatus === 'expired') {
                    const { tryAddExpiredCustomer, getExpiredCustomerCount } = await import('./utils/expiredActionLimit');
                    const handleLimitExceeded = async () => {
                        const count = await getExpiredCustomerCount();
                        alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${count}명 등록되어 있습니다.\n계속 사용하려면 사용신청을 해주세요.`);
                        try {
                            window.dispatchEvent(new Event('openApprovalModal'));
                        } catch (_) { }
                    };

                    const canAdd = await tryAddExpiredCustomer(null, handleLimitExceeded);
                    if (!canAdd) return;
                }

                const customer = {
                    id: crypto.randomUUID(),
                    name,
                    phone,
                    memo,
                    stage: 'prospect',
                    createdAt: new Date().toISOString()
                };

                // IndexedDB 및 상태 반영
                await addCustomer(customer);

                // 히스토리 초기화 (필요 시)
                try {
                    const { ensureHistoryInitialized } = await import('./hooks/useCustomerHistory');
                    await ensureHistoryInitialized(customer.id);
                } catch (_) { }

                // 활동 로그 기록
                try {
                    await sendActionLog('고객_추가', {
                        source: 'onboarding',
                        customerName: name,
                        targetStage: '가망고객'
                    });
                } catch (_) { }

                // 온보딩 완료 플래그는 성공 저장 후에만 설정
                try { localStorage.setItem('onb_completed', '1'); } catch (_) { }

                // 첫 고객이면 NextAction 자동 오픈
                try {
                    const currentCount = Number(localStorage.getItem('totalCustomersAdded') || '0');
                    const nextCount = currentCount + 1;
                    localStorage.setItem('totalCustomersAdded', String(nextCount));
                    if (nextCount === 1) {
                        setNextAction({ open: true, customer });
                    }
                } catch (_) { setNextAction({ open: true, customer }); }
            } catch (err) {
                console.error('온보딩 고객 생성 처리 실패:', err);
            }
        };
        window.addEventListener('createOnboardingCustomer', handler);
        return () => window.removeEventListener('createOnboardingCustomer', handler);
    }, [addCustomer, sendActionLog, appStatus]);

    // 업종 변경 이벤트 구독
    useEffect(() => {
        const handler = (e) => {
            try {
                const bt = (e && e.detail && e.detail.businessType) || localStorage.getItem('businessType') || 'insurance';
                setBusinessType(bt);
            } catch (_) { }
        };
        window.addEventListener('businessTypeChanged', handler);
        return () => window.removeEventListener('businessTypeChanged', handler);
    }, []);

    // 하루에 한 번 고객뱅크에서 고객 1명을 연락대상으로 이동하고 '추천' 배지 추가
    useEffect(() => {
        const dailyRecommendCustomer = async (force = false) => {
            // 고객 데이터가 로드되지 않았으면 실행하지 않음
            if (customersLoading || !customers) return;

            try {
                // KST 기준으로 오늘 날짜 계산
                const now = new Date();
                const kstTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
                const yyyy = String(kstTime.getFullYear());
                const mm = String(kstTime.getMonth() + 1).padStart(2, '0');
                const dd = String(kstTime.getDate()).padStart(2, '0');
                const todayStr = `${yyyy}-${mm}-${dd}`;

                // 마지막 실행 날짜 확인
                const lastExecutedDate = localStorage.getItem('dailyRecommendCustomerLastExecuted');

                // 오늘 이미 실행했으면 실행하지 않음 (강제 실행이 아닌 경우)
                if (!force && lastExecutedDate === todayStr) return;

                // 고객뱅크에서 고객 목록 가져오기
                const bankCustomers = customers['bank'] || [];

                // 고객뱅크에 고객이 없으면 실행하지 않음
                if (bankCustomers.length === 0) return;

                // 랜덤으로 고객 1명 선택
                const randomIndex = Math.floor(Math.random() * bankCustomers.length);
                const selectedCustomer = bankCustomers[randomIndex];

                if (!selectedCustomer) return;

                // 고객 업데이트: 연락대상으로 이동하고 '추천' 배지 추가
                const updatedCustomer = {
                    ...selectedCustomer,
                    stage: 'preparation',
                    recommended: true,  // 추천 배지 표시를 위한 플래그
                    recommendedDate: new Date().toISOString()  // 추천된 날짜 저장
                };

                // 1) 화면 상태 업데이트
                const newCustomers = {
                    ...customers,
                    bank: bankCustomers.filter(c => c.id !== selectedCustomer.id),
                    preparation: [...(customers['preparation'] || []).filter(c => c.id !== selectedCustomer.id), updatedCustomer]
                };
                setCustomers(newCustomers);

                // 2) 영구 저장(IndexedDB)
                try {
                    await updateCustomer(selectedCustomer.id, updatedCustomer);
                } catch (e) {
                    console.warn('[dailyRecommendCustomer] persist failed, reverting UI state', e);
                    // 실패 시 롤백
                    setCustomers(customers);
                    return;
                }

                // 3) 마지막 실행 날짜 업데이트
                localStorage.setItem('dailyRecommendCustomerLastExecuted', todayStr);

                // 4) 액션 로그 전송
                try {
                    await sendActionLog('고객_일일_추천', {
                        customerName: selectedCustomer.name,
                        fromStage: '고객뱅크',
                        toStage: '연락대상',
                        badge: '추천'
                    });
                } catch (_) { }

                // 5) GA4 이벤트 전송
                try {
                    const gtag = window.gtag;
                    const measurementId = window.GA_MEASUREMENT_ID;
                    if (typeof gtag === 'function' && measurementId) {
                        const deviceId = generateDeviceId();
                        gtag('event', 'daily_recommend_customer', {
                            customer_id: selectedCustomer.id,
                            from_stage: 'bank',
                            to_stage: 'preparation',
                            badge: '추천',
                            device_id: deviceId
                        });
                    }
                } catch (_) { }

            } catch (error) {
                console.error('일일 추천 고객 처리 실패:', error);
            }
        };

        dailyRecommendCustomer();
    }, [customersLoading, customers, setCustomers, updateCustomer, sendActionLog]);




    function safeGetStages(bt) {
        try { return getCustomerStages(bt); } catch (_) { return DEFAULT_STAGES; }
    }

    // (앞쪽에 위치했던 뒤로가기 useEffect는 제거됨)

    // 디버깅 함수 등록
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        window.debugCustomers = () => {
            console.log('=== 고객 데이터 디버깅 ===');
            console.log('전체 고객:', customers);
            console.log('로딩 상태:', customersLoading);
            console.log('에러:', customersError);
        };

        window.debugHistory = async () => {
            console.log('=== 히스토리 디버깅 ===');
            try {
                const { idbGetAllHistories } = await import('./utils/idb');
                const allHistories = await idbGetAllHistories();
                console.log('전체 히스토리:', allHistories);

                const withFollowUp = allHistories.filter(h => h.followUp);
                console.log('Follow Up이 있는 히스토리:', withFollowUp);
            } catch (e) {
                console.error('히스토리 디버깅 실패:', e);
            }
        };

        // 일일 추천 고객 기능 강제 실행 (디버깅용)
        window.forceDailyRecommend = async () => {
            console.log('🔧 일일 추천 고객 기능 강제 실행 중...');

            if (customersLoading || !customers) {
                console.warn('⚠️ 고객 데이터가 아직 로드되지 않았습니다.');
                return;
            }

            const bankCustomers = customers['bank'] || [];
            if (bankCustomers.length === 0) {
                console.warn('⚠️ 고객뱅크에 고객이 없습니다.');
                return;
            }

            try {
                // 랜덤으로 고객 1명 선택
                const randomIndex = Math.floor(Math.random() * bankCustomers.length);
                const selectedCustomer = bankCustomers[randomIndex];

                if (!selectedCustomer) {
                    console.warn('⚠️ 선택된 고객이 없습니다.');
                    return;
                }

                console.log(`✅ 선택된 고객: ${selectedCustomer.name} (${selectedCustomer.id})`);

                // 고객 업데이트: 연락대상으로 이동하고 '추천' 배지 추가
                const updatedCustomer = {
                    ...selectedCustomer,
                    stage: 'preparation',
                    recommended: true,
                    recommendedDate: new Date().toISOString()  // 추천된 날짜 저장
                };

                // 1) 화면 상태 업데이트
                const newCustomers = {
                    ...customers,
                    bank: bankCustomers.filter(c => c.id !== selectedCustomer.id),
                    preparation: [...(customers['preparation'] || []).filter(c => c.id !== selectedCustomer.id), updatedCustomer]
                };
                setCustomers(newCustomers);

                // 2) 영구 저장(IndexedDB)
                try {
                    await updateCustomer(selectedCustomer.id, updatedCustomer);
                    console.log('✅ IndexedDB에 저장 완료');
                } catch (e) {
                    console.error('❌ IndexedDB 저장 실패:', e);
                    setCustomers(customers);
                    return;
                }

                // 3) 액션 로그 전송
                try {
                    await sendActionLog('고객_일일_추천', {
                        customerName: selectedCustomer.name,
                        fromStage: '고객뱅크',
                        toStage: '연락대상',
                        badge: '추천',
                        forced: true
                    });
                } catch (_) { }

                // 4) GA4 이벤트 전송
                try {
                    const gtag = window.gtag;
                    const measurementId = window.GA_MEASUREMENT_ID;
                    if (typeof gtag === 'function' && measurementId) {
                        const deviceId = generateDeviceId();
                        gtag('event', 'daily_recommend_customer', {
                            customer_id: selectedCustomer.id,
                            from_stage: 'bank',
                            to_stage: 'preparation',
                            badge: '추천',
                            forced: true,
                            device_id: deviceId
                        });
                    }
                } catch (_) { }

                console.log('✅ 일일 추천 고객 기능 실행 완료!');
            } catch (error) {
                console.error('❌ 일일 추천 고객 처리 실패:', error);
            }
        };

        // 체험 사용자 고객 등록 유도 모달 디버깅 함수
        window.debugTrialModal = () => {
            console.log('=== 체험 사용자 고객 등록 유도 모달 디버깅 ===');
            console.log('모달 표시 상태:', showTrialRegistrationModal);
            console.log('앱 상태:', appStatus);
            console.log('체험 정보:', trialInfo);
            console.log('고객 로딩 상태:', customersLoading);

            // 고객 수 계산
            const totalCustomers = Object.values(customers).reduce(
                (sum, stageCustomers) => sum + (stageCustomers?.length || 0),
                0
            );
            console.log('전체 고객 수:', totalCustomers);

            // 체험 상태 확인
            const isTrialUser =
                appStatus === 'trial' ||
                appStatus === 'expired' ||
                (appStatus === 'pending' && trialInfo?.status === 'trial');
            console.log('체험 사용자 여부:', isTrialUser);

            // 날짜 확인
            const now = new Date();
            const kstTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
            const yyyy = String(kstTime.getFullYear());
            const mm = String(kstTime.getMonth() + 1).padStart(2, '0');
            const dd = String(kstTime.getDate()).padStart(2, '0');
            const today = `${yyyy}-${mm}-${dd}`;
            const lastShownDate = localStorage.getItem('trialRegistrationModalLastShown');
            console.log('오늘 날짜:', today);
            console.log('마지막 표시 날짜:', lastShownDate || '없음');
            console.log('오늘 표시 여부:', lastShownDate === today);

            // 모달 표시 조건 확인
            const shouldShow = isTrialUser && totalCustomers === 0 && lastShownDate !== today;
            console.log('모달 표시 조건 만족:', shouldShow);
        };

        // 모달 강제 표시 (테스트용)
        window.showTrialModal = () => {
            setShowTrialRegistrationModal(true);
            console.log('✅ 체험 사용자 고객 등록 유도 모달을 강제로 표시했습니다.');
        };

        // 모달 표시 기록 초기화 (테스트용)
        window.resetTrialModalDate = () => {
            localStorage.removeItem('trialRegistrationModalLastShown');
            console.log('✅ 모달 표시 날짜 기록을 초기화했습니다. 페이지를 새로고침하면 모달이 다시 표시될 수 있습니다.');
        };

        window.resetData = async () => {
            try {
                const { clearAllData } = await import('./utils/idb');
                await clearAllData();
                console.log('모든 데이터 초기화 완료. 페이지를 새로고침하세요.');
            } catch (e) {
                console.error('데이터 초기화 실패:', e);
            }
        };

        window.forceMigration = async () => {
            try {
                const { migrateAllLocalStorageData } = await import('./utils/idb');
                await migrateAllLocalStorageData();
                console.log('강제 마이그레이션 완료');
                reloadCustomers();
            } catch (e) {
                console.error('마이그레이션 실패:', e);
            }
        };


        window.validateData = async () => {
            try {
                const { validateDataIntegrity } = await import('./utils/idb');
                const result = await validateDataIntegrity();
                console.log('데이터 무결성 검증 결과:', result);
                return result;
            } catch (e) {
                console.error('데이터 검증 실패:', e);
            }
        };

        window.repairData = async () => {
            try {
                const { repairDataIntegrity } = await import('./utils/idb');
                const result = await repairDataIntegrity();
                console.log('데이터 수리 결과:', result);
                if (result.success) {
                    reloadCustomers();
                }
                return result;
            } catch (e) {
                console.error('데이터 수리 실패:', e);
            }
        };

        window.getMigrationStatus = async () => {
            try {
                const { idbGetSetting } = await import('./utils/idb');
                const status = {
                    customersMigrated: await idbGetSetting('customers_migrated'),
                    historiesMigrated: await idbGetSetting('histories_migrated'),
                    customersMigrationDate: await idbGetSetting('customers_migration_date'),
                    historiesMigrationDate: await idbGetSetting('histories_migration_date'),
                    sampleDataCreated: await idbGetSetting('sample_data_created'),
                    hasLocalStorageCustomers: !!localStorage.getItem('customerData'),
                    hasLocalStorageHistories: !!localStorage.getItem('customerHistory')
                };
                console.log('마이그레이션 상태:', status);
                return status;
            } catch (e) {
                console.error('마이그레이션 상태 확인 실패:', e);
            }
        };

        // (기존 즉시 등록 popstate 핸들러 제거됨 — 지연 등록 핸들러만 사용)
        // 이 useEffect의 정리 함수는 아래 지연 등록 블록에서 정의됩니다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customers]);

    // 푸시 알림 훅 제거 (설정 모달에서 관리)

    // 사용자 정보 상태 추가
    const [userName, setUserName] = useState('');
    const [nextBillingDate, setNextBillingDate] = useState(null);

    // UI 관련 상태
    const [showAddForm, setShowAddForm] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showKakaoMap, setShowKakaoMap] = useState(false);
    const [showBrowserModal, setShowBrowserModal] = useState(false);
    const [showShareBrowserModal, setShowShareBrowserModal] = useState(false);
    const [showSideMenu, setShowSideMenu] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const [showAssistant, setShowAssistant] = useState(false);
    const [showReportGenerator, setShowReportGenerator] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [targetStageForAdd, setTargetStageForAdd] = useState(null);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [showListToggle, setShowListToggle] = useState(false);
    const [hideFloatingForMap, setHideFloatingForMap] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showMigrationAlert, setShowMigrationAlert] = useState(false);
    const [showListView, setShowListView] = useState(false);
    const [showBulkKakaoModal, setShowBulkKakaoModal] = useState(false);
    const [bulkKakaoMessage, setBulkKakaoMessage] = useState('');
    const [showBulkCustomerInputModal, setShowBulkCustomerInputModal] = useState(false);
    const [bulkKakaoType, setBulkKakaoType] = useState('text'); // 'text' 또는 'image'
    const [bulkKakaoImage, setBulkKakaoImage] = useState(null);
    const [bulkKakaoImageFile, setBulkKakaoImageFile] = useState(null);

    // 모달 상태들
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        action: null,
        customer: null,
        message: ''
    });
    const [deleteModal, setDeleteModal] = useState({
        show: false,
        customer: null
    });
    const [bulkMoveModal, setBulkMoveModal] = useState({
        show: false,
        customers: []
    });
    const [moveModal, setMoveModal] = useState({
        show: false,
        customer: null
    });

    // 환영 모달 상태
    const [showWelcomeModal, setShowWelcomeModal] = useState(() => localStorage.getItem('welcome_modal_shown') === '1' ? false : true);
    // 다음 행동 모달 상태
    const [nextAction, setNextAction] = useState({ open: false, customer: null });
    // 체험 사용자 고객 등록 유도 모달 상태
    const [showTrialRegistrationModal, setShowTrialRegistrationModal] = useState(false);

    // 뒤로가기용 현재 UI 상태를 저장하는 ref (리스너는 고정, 값은 최신 상태 반영)
    const uiStateRef = useRef({});
    // 종료 확인 모달 닫기 직후 재오픈 방지용 플래그
    const suppressExitPromptRef = useRef(false);
    useEffect(() => {
        uiStateRef.current = {
            showSideMenu,
            showHelpModal,
            showBrowserModal,
            showNotice,
            showAssistant,
            showReportGenerator,
            showSettingsModal,
            showImportModal,
            showTermsModal,
            showPrivacyModal,
            showMigrationAlert,
            showWelcomeModal,
            showTrialRegistrationModal,
            confirmModalShow: confirmModal.show,
            deleteModalShow: deleteModal.show,
            moveModalShow: moveModal.show,
            bulkMoveModalShow: bulkMoveModal.show,
            nextActionOpen: nextAction.open
        };
    }, [
        showSideMenu, showHelpModal, showBrowserModal, showNotice, showAssistant, showReportGenerator,
        showSettingsModal, showImportModal, showTermsModal, showPrivacyModal, showMigrationAlert,
        showWelcomeModal, showTrialRegistrationModal, confirmModal.show, deleteModal.show, moveModal.show, bulkMoveModal.show, nextAction.open
    ]);

    // 안드로이드 뒤로가기: 마운트 시 1회 등록, 항상 최신 상태는 uiStateRef로 확인
    useEffect(() => {
        const pushStateOnce = () => {
            try { window.history.pushState(null, '', window.location.href); } catch (_) { }
        };

        const onPopState = () => {
            const s = uiStateRef.current || {};
            // 열려있는 UI가 있으면 닫기 우선
            let closed = false;
            try {
                if (s.showSideMenu) { setShowSideMenu(false); closed = true; }
                if (s.showHelpModal) { setShowHelpModal(false); closed = true; }
                if (s.showBrowserModal) { setShowBrowserModal(false); closed = true; }
                if (s.showNotice) { setShowNotice(false); closed = true; }
                if (s.showAssistant) { setShowAssistant(false); closed = true; }
                if (s.showReportGenerator) { setShowReportGenerator(false); closed = true; }
                if (s.showSettingsModal) { setShowSettingsModal(false); closed = true; }
                if (s.showImportModal) { setShowImportModal(false); closed = true; }
                if (s.showTermsModal) { setShowTermsModal(false); closed = true; }
                if (s.showPrivacyModal) { setShowPrivacyModal(false); closed = true; }
                if (s.showMigrationAlert) { setShowMigrationAlert(false); closed = true; }
                if (s.showWelcomeModal) { setShowWelcomeModal(false); closed = true; }
                if (s.showTrialRegistrationModal) { setShowTrialRegistrationModal(false); closed = true; }
                if (s.confirmModalShow) {
                    setConfirmModal({ show: false, action: null, customer: null, message: '' });
                    closed = true;
                    // 종료 모달이 열려 있었다면 즉시 재오픈되지 않도록 잠시 억제
                    suppressExitPromptRef.current = true;
                    setTimeout(() => { suppressExitPromptRef.current = false; }, 300);
                }
                if (s.deleteModalShow) { setDeleteModal({ show: false, customer: null }); closed = true; }
                if (s.moveModalShow) { setMoveModal({ show: false, customer: null }); closed = true; }
                if (s.bulkMoveModalShow) { setBulkMoveModal({ show: false, customers: [] }); closed = true; }
                if (s.nextActionOpen) { setNextAction({ open: false, customer: null }); closed = true; }
            } catch (_) { }

            if (closed) { pushStateOnce(); return; }

            // 종료 확인 모달 표시 (최근에 닫혔던 경우 잠시 억제)
            if (suppressExitPromptRef.current) { pushStateOnce(); return; }
            setConfirmModal({
                show: true,
                action: 'exit_app',
                customer: null,
                message: '앱을 정말 종료하시겠습니까?'
            });
            pushStateOnce();
        };

        pushStateOnce();
        window.addEventListener('popstate', onPopState);
        window.__fcfunnel_popstate_cleanup__ = () => window.removeEventListener('popstate', onPopState);
        return () => { try { window.__fcfunnel_popstate_cleanup__ && window.__fcfunnel_popstate_cleanup__(); } catch (_) { } };
    }, []);

    // 스크롤 위치 감지
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            setShowScrollToTop(scrollTop > 300);
            // 가이드: 가망고객 섹션 근처에서 표시(대략 헤더/배너를 지나 600px 이후)
            setShowListToggle(scrollTop > 600);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 카카오맵 모달 열림/닫힘에 따라 플로팅 버튼 숨김
    useEffect(() => {
        const onOpen = () => setHideFloatingForMap(true);
        const onClose = () => setHideFloatingForMap(false);
        window.addEventListener('kakaoMapOpen', onOpen);
        window.addEventListener('kakaoMapClose', onClose);
        return () => {
            window.removeEventListener('kakaoMapOpen', onOpen);
            window.removeEventListener('kakaoMapClose', onClose);
        };
    }, []);

    // 첫 방문 시 도움말 모달 자동 표시 (다른 모달/사이드바가 없을 때까지 대기 후 1회 표시)
    // 비활성화됨 - 도움말 모달은 사용자가 직접 열어야 함
    // useEffect(() => {
    //     try {
    //         const seen = localStorage.getItem('hasSeenHelp');
    //         if (seen === 'true') return;

    //         let cancelled = false;
    //         let attempts = 0;
    //         let timerId;

    //         const tryOpen = () => {
    //             if (cancelled) return;
    //             attempts += 1;

    //             // 현재 열려있는 UI 상태 확인 (uiStateRef는 항상 최신 상태를 가리킴)
    //             const s = uiStateRef.current || {};
    //             const hasBlockingUi = !!(
    //                 s.showSideMenu ||
    //                 s.showBrowserModal ||
    //                 s.showNotice ||
    //                 s.showAssistant ||
    //                 s.showReportGenerator ||
    //                 s.showSettingsModal ||
    //                 s.showImportModal ||
    //                 s.showTermsModal ||
    //                 s.showPrivacyModal ||
    //                 s.showMigrationAlert ||
    //                 s.showWelcomeModal ||
    //                 s.confirmModalShow ||
    //                 s.deleteModalShow ||
    //                 s.moveModalShow ||
    //                 s.nextActionOpen
    //             );

    //             if (!hasBlockingUi) {
    //                 setShowHelpModal(true);
    //                 return;
    //             }

    //             // 최대 20회(약 20초)까지 재시도
    //             if (attempts < 20) {
    //                 timerId = setTimeout(tryOpen, 1000);
    //             }
    //         };

    //         timerId = setTimeout(tryOpen, 800);
    //         return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
    //     } catch (_) { /* noop */ }
    // }, []);

    // 카카오톡 인앱 브라우저 감지 및 알림 표시
    useEffect(() => {
        try {
            // 카카오톡 인앱 브라우저인지 확인
            if (isKakaoInAppBrowser()) {
                // 세션당 한 번만 표시
                const hasShown = sessionStorage.getItem('kakaoBrowserModalShown');
                if (!hasShown) {
                    // 다른 모달이 모두 닫힌 후에 표시
                    const checkAndShow = () => {
                        const s = uiStateRef.current || {};
                        const hasBlockingUi = !!(
                            s.showSideMenu ||
                            s.showBrowserModal ||
                            s.showNotice ||
                            s.showAssistant ||
                            s.showReportGenerator ||
                            s.showSettingsModal ||
                            s.showImportModal ||
                            s.showTermsModal ||
                            s.showPrivacyModal ||
                            s.showMigrationAlert ||
                            s.showWelcomeModal ||
                            s.confirmModalShow ||
                            s.deleteModalShow ||
                            s.moveModalShow ||
                            s.bulkMoveModalShow ||
                            s.nextActionOpen
                        );

                        if (!hasBlockingUi) {
                            setShowBrowserModal(true);
                            sessionStorage.setItem('kakaoBrowserModalShown', '1');
                        } else {
                            // 아직 다른 모달이 열려있으면 1초 후 다시 확인
                            setTimeout(checkAndShow, 1000);
                        }
                    };

                    // 2초 후에 표시 (앱 로딩 완료 후)
                    setTimeout(checkAndShow, 2000);
                }
            }
        } catch (_) { /* noop */ }
    }, []);

    // (streak 기능 제거)

    // 사용자 이름/다음결제일 로드 (정식버전일 때만)
    useEffect(() => {
        if (appStatus === 'active') {
            // 기기 링크 정보가 있으면 추가기기에서 본 기기의 사용자명 가져오기
            if (deviceLinkInfo && deviceLinkInfo.isSecondary && deviceLinkInfo.userInfo) {
                setUserName(deviceLinkInfo.userInfo.name || '');
            } else {
                // 본 기기인 경우 localStorage에서 사용자명 가져오기
                const savedUserInfo = localStorage.getItem('userInfo');
                if (savedUserInfo) {
                    try {
                        const userInfo = JSON.parse(savedUserInfo);
                        setUserName(userInfo.name || '');
                    } catch (error) {
                        console.error('사용자 정보 로드 실패:', error);
                    }
                }
            }

            // 기기 문서에서 다음결제일 조회
            (async () => {
                try {
                    const { doc, getDoc } = await import('firebase/firestore');
                    const deviceId = localStorage.getItem('deviceId');
                    if (!deviceId) return;
                    const snap = await getDoc(doc(db, 'devices', deviceId));
                    if (snap.exists()) {
                        const data = snap.data();
                        if (data.nextBillingDate) setNextBillingDate(data.nextBillingDate);
                    }
                } catch (e) {
                    console.warn('다음결제일 조회 실패', e);
                }
            })();
        }
    }, [appStatus, deviceLinkInfo]);

    // 새로운 공지사항 체크 함수 (다른 모달과 충돌 방지)
    const checkForNewNotices = useCallback(async () => {
        try {
            // 다른 모달이 열려있는지 확인
            const s = uiStateRef.current || {};
            const hasBlockingUi = !!(
                s.showSideMenu ||
                s.showBrowserModal ||
                s.showNotice ||
                s.showAssistant ||
                s.showReportGenerator ||
                s.showSettingsModal ||
                s.showImportModal ||
                s.showTermsModal ||
                s.showPrivacyModal ||
                s.showMigrationAlert ||
                s.showWelcomeModal ||
                s.confirmModalShow ||
                s.deleteModalShow ||
                s.moveModalShow ||
                s.bulkMoveModalShow ||
                s.nextActionOpen
            );

            // 다른 모달이 열려있으면 나중에 다시 시도
            if (hasBlockingUi) {
                setTimeout(checkForNewNotices, 2000); // 2초 후 재시도
                return;
            }

            const { collection, getDocs } = await import('firebase/firestore');
            const snapshot = await getDocs(collection(db, 'notices'));
            const activeNotices = [];

            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.isActive === true) {
                    activeNotices.push({ id: doc.id, ...data });
                }
            });

            if (activeNotices.length === 0) return;

            // 가장 최근 공지사항의 생성 시간
            const latestNotice = activeNotices.sort((a, b) =>
                new Date(b.createdAt) - new Date(a.createdAt)
            )[0];

            // 사용자가 마지막으로 공지사항을 확인한 시간
            const lastCheckedTime = localStorage.getItem('lastNoticeChecked');

            if (!lastCheckedTime || new Date(latestNotice.createdAt) > new Date(lastCheckedTime)) {
                // 다른 모달이 여전히 열려있는지 다시 확인
                const s2 = uiStateRef.current || {};
                const stillHasBlockingUi = !!(
                    s2.showSideMenu ||
                    s2.showBrowserModal ||
                    s2.showNotice ||
                    s2.showAssistant ||
                    s2.showReportGenerator ||
                    s2.showSettingsModal ||
                    s2.showImportModal ||
                    s2.showTermsModal ||
                    s2.showPrivacyModal ||
                    s2.showMigrationAlert ||
                    s2.showWelcomeModal ||
                    s2.confirmModalShow ||
                    s2.deleteModalShow ||
                    s2.moveModalShow ||
                    s2.bulkMoveModalShow ||
                    s2.nextActionOpen
                );

                if (!stillHasBlockingUi) {
                    // 새로운 공지사항이 있으면 자동으로 표시
                    setTimeout(() => {
                        // 마지막 한 번 더 확인
                        const s3 = uiStateRef.current || {};
                        const finalCheck = !!(
                            s3.showSideMenu ||
                            s3.showBrowserModal ||
                            s3.showNotice ||
                            s3.showAssistant ||
                            s3.showReportGenerator ||
                            s3.showSettingsModal ||
                            s3.showImportModal ||
                            s3.showTermsModal ||
                            s3.showPrivacyModal ||
                            s3.showMigrationAlert ||
                            s3.showWelcomeModal ||
                            s3.confirmModalShow ||
                            s3.deleteModalShow ||
                            s3.moveModalShow ||
                            s3.bulkMoveModalShow ||
                            s3.nextActionOpen
                        );
                        if (!finalCheck) {
                            setShowNotice(true);
                        } else {
                            // 여전히 다른 모달이 열려있으면 재시도
                            setTimeout(checkForNewNotices, 2000);
                        }
                    }, 1000); // 1초 후에 표시 (앱 로딩 완료 후)
                } else {
                    // 여전히 다른 모달이 열려있으면 재시도
                    setTimeout(checkForNewNotices, 2000);
                }
            }
        } catch (error) {
            console.error('공지사항 체크 실패:', error);
        }
    }, []);

    // 공지사항 모달을 닫을 때 확인 시간 업데이트
    const handleNoticeClose = () => {
        localStorage.setItem('lastNoticeChecked', new Date().toISOString());
        setShowNotice(false);
    };

    // 앱 로드 시 공지사항 체크 (다른 모달과 충돌 방지)
    useEffect(() => {
        // 앱이 활성화된 후에만 체크
        if (appStatus === 'active' || appStatus === 'trial' || appStatus === 'expired' || appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
            // 앱 로딩 완료 후 약간의 지연을 두고 체크 (다른 모달들이 먼저 표시될 시간 확보)
            const timer = setTimeout(() => {
                checkForNewNotices();
            }, 3000); // 3초 후 체크

            return () => clearTimeout(timer);
        }
    }, [appStatus, checkForNewNotices]);

    // 체험 텍스트 랜덤 변경 로직 제거(미사용)

    // 포그라운드 메시지 리스너 설정 (토큰 등록은 App.js에서 처리)
    useEffect(() => {
        // active, trial, expired, subscription_expired 사용자도 푸시 알림 수신 가능
        if (appStatus === 'active' || appStatus === 'trial' || appStatus === 'expired' || appStatus === DEVICE_STATUS.SUBSCRIPTION_EXPIRED) {
            // 포그라운드 메시지 리스너 설정
            setupMessageListener(async (payload) => {
                console.log('포그라운드에서 푸시 알림 수신:', payload);

                // 기본 알림 정보
                let notificationBody = payload.notification?.body || payload.data?.body || '';
                let notificationTitle = payload.notification?.title || payload.data?.title || "FC퍼널's AI비서";

                // 생일 알림인 경우 로컬에서 생일 고객 이름 조회
                if (payload?.data?.intent === 'open_ai_birthday') {
                    try {
                        const { idbGetAllCustomers } = await import('./utils/idb');
                        const allCustomers = await idbGetAllCustomers();

                        // 로컬 시간대 기준으로 오늘 날짜 계산 (AI비서 모달과 동일한 방식)
                        const today = new Date();
                        const todayMonth = today.getMonth() + 1; // 0-based to 1-based
                        const todayDay = today.getDate();

                        console.log('[푸시알림] 디버깅 시작 - 오늘 날짜:', todayMonth + '/' + todayDay);
                        console.log('[푸시알림] 전체 고객 수:', allCustomers.length);

                        // 생일이 있는 고객들만 필터링해서 로그 출력
                        const customersWithBirthdate = allCustomers.filter(c => c?.birthdate);
                        console.log('[푸시알림] 생일 정보가 있는 고객 수:', customersWithBirthdate.length);
                        if (customersWithBirthdate.length > 0) {
                            console.log('[푸시알림] 생일 정보 샘플 (최대 5개):',
                                customersWithBirthdate.slice(0, 5).map(c => ({
                                    name: c.name,
                                    birthdate: c.birthdate,
                                    birthdateType: typeof c.birthdate
                                }))
                            );
                        }

                        // 생일 날짜 비교 함수 (시간대 문제 방지) - AI비서 모달과 동일한 로직
                        const isBirthdayToday = (birthdateValue) => {
                            if (!birthdateValue) return false;

                            let month, day;

                            // 문자열 형식인 경우
                            if (typeof birthdateValue === 'string') {
                                // 앞뒤 공백 제거
                                const trimmed = birthdateValue.trim();
                                const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
                                if (match) {
                                    month = parseInt(match[2], 10);
                                    day = parseInt(match[3], 10);
                                } else {
                                    // 다른 형식의 문자열인 경우 Date 객체로 파싱 시도
                                    const birth = new Date(trimmed);
                                    if (isNaN(birth.getTime())) return false;
                                    month = birth.getMonth() + 1;
                                    day = birth.getDate();
                                }
                            } else {
                                // Date 객체나 타임스탬프인 경우
                                const birth = new Date(birthdateValue);
                                if (isNaN(birth.getTime())) return false;
                                month = birth.getMonth() + 1;
                                day = birth.getDate();
                            }

                            const isMatch = month === todayMonth && day === todayDay;
                            if (isMatch) {
                                console.log('[푸시알림] 생일 매칭 성공:', { birthdateValue, month, day, todayMonth, todayDay });
                            }
                            return isMatch;
                        };

                        const birthdayCustomers = allCustomers.filter(customer => {
                            if (!customer.birthdate) return false;
                            return isBirthdayToday(customer.birthdate);
                        });

                        console.log('[푸시알림] 최종 생일 고객 수:', birthdayCustomers.length);
                        if (birthdayCustomers.length > 0) {
                            console.log('[푸시알림] 생일 고객 목록:', birthdayCustomers.map(c => ({ name: c.name, birthdate: c.birthdate })));
                        }

                        const birthdayNames = birthdayCustomers.map(c => c.name || '고객');

                        if (birthdayCustomers.length === 0) {
                            notificationBody = '오늘은 생일을 맞이한 고객이 없습니다';
                        } else if (birthdayNames.length === 1) {
                            notificationBody = `${birthdayNames[0]}님의 생일입니다 🎉`;
                        } else if (birthdayNames.length <= 3) {
                            notificationBody = `${birthdayNames.join(', ')}님의 생일입니다 🎉`;
                        } else {
                            notificationBody = `${birthdayNames.slice(0, 3).join(', ')}님 외 ${birthdayNames.length - 3}명의 생일입니다 🎉`;
                        }
                    } catch (error) {
                        console.error('생일 고객 조회 실패:', error);
                        // 실패 시 원본 메시지 유지
                    }
                }

                // 커스텀 알림 표시 (생일 알림이거나 원본 알림이 있는 경우)
                if (payload?.data?.intent === 'open_ai_birthday' || payload.notification || (payload.data?.body && notificationBody)) {
                    const notification = new Notification(notificationTitle, {
                        body: notificationBody,
                        icon: '/logo192.png',
                        tag: 'fcfunnel-push'
                    });

                    notification.onclick = () => {
                        window.focus();
                        notification.close();
                    };
                }

                // intent 처리: AI비서 생일 탭 오픈
                try {
                    if (payload?.data?.intent === 'open_ai_birthday') {
                        console.log('[CustomerManagement] intent=open_ai_birthday → 모달 오픈');
                        setShowAssistant(true);
                        // 탭 전환 이벤트 브로드캐스트
                        window.dispatchEvent(new CustomEvent('openAssistant', { detail: { tab: 'birthday' } }));
                    }
                } catch (_) { }
            });
        }
    }, [appStatus]);

    // 기기 링크 정보 로드
    useEffect(() => {
        const loadDeviceLinkInfo = async () => {
            try {
                const primaryId = localStorage.getItem('deviceId');
                if (!primaryId) return;
                const info = await getDeviceLinkInfo(primaryId);
                setDeviceLinkInfo(info);
            } catch (e) {
                console.warn('기기 링크 정보 로드 실패', e);
            }
        };

        if (appStatus === 'active' || appStatus === 'trial') {
            loadDeviceLinkInfo();
        }
    }, [appStatus]);

    // 백그라운드 알림 클릭으로 열린 경우: URL 파라미터로 모달 자동 오픈
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const assistant = params.get('assistant');
            if (assistant === 'ai') {
                const tab = params.get('tab') || 'birthday';
                console.log('[CustomerManagement] URL param detected -> open assistant modal, tab:', tab);
                setShowAssistant(true);
                // 탭 지정 이벤트 (AssistantModal 내부에서 수신하도록 구현되어 있어야 함)
                window.dispatchEvent(new CustomEvent('openAssistant', { detail: { tab } }));
            }
        } catch (e) { /* noop */ }
    }, []);

    // 정식버전 라벨은 고정 표기 (크기 변동 방지)

    // 폼 관련 핸들러
    const handleShowAddForm = (targetStage = null) => {
        setTargetStageForAdd(targetStage);
        setShowAddForm(true);
    };

    const handleEditCustomer = (customer) => {
        setEditingCustomer(customer);
        setShowAddForm(true);
    };

    const handleCloseForm = () => {
        setShowAddForm(false);
        setEditingCustomer(null);
        setTargetStageForAdd(null);
    };

    // 고객 이동 관련 함수들
    const moveCustomer = async (customer, fromStage, toStage) => {
        const updatedCustomer = { ...customer, stage: toStage };

        // 1) 화면 상태 업데이트
        const newCustomers = {
            ...customers,
            [fromStage]: (customers[fromStage] || []).filter(c => c.id !== customer.id),
            [toStage]: [...(customers[toStage] || []).filter(c => c.id !== customer.id), updatedCustomer]
        };
        setCustomers(newCustomers);

        // 2) 영구 저장(IndexedDB)
        try {
            await updateCustomer(customer.id, updatedCustomer);
        } catch (e) {
            console.warn('[moveCustomer] persist failed, reverting UI state', e);
        }

        // 3) 액션 로그 전송
        sendActionLog('고객_단계_이동', {
            customerName: customer.name,
            fromStage: CUSTOMER_STAGES.find(s => s.id === fromStage)?.title || fromStage,
            toStage: CUSTOMER_STAGES.find(s => s.id === toStage)?.title || toStage,
            direction: 'direct'
        });

        // GA4: 고객 단계 변경 이벤트
        try {
            const gtag = window.gtag;
            const measurementId = window.GA_MEASUREMENT_ID;
            if (typeof gtag === 'function' && measurementId) {
                const deviceId = generateDeviceId();
                // 일반 단계 변경 이벤트
                gtag('event', 'stage_change', {
                    customer_id: customer.id,
                    from_stage: fromStage,
                    to_stage: toStage,
                    priority: customer.priority || 'medium',
                    value: Number(customer.value) || 0,
                    device_id: deviceId
                });
            }
        } catch (_) { }
    };

    const moveCustomerForward = (customer) => {
        if (currentStep >= CUSTOMER_STAGES.length - 1) return;

        const fromStage = CUSTOMER_STAGES[currentStep].id;
        const toStage = CUSTOMER_STAGES[currentStep + 1].id;
        moveCustomer(customer, fromStage, toStage);
    };

    const moveCustomerBack = (customer) => {
        if (currentStep <= 0) return;

        const fromStage = CUSTOMER_STAGES[currentStep].id;
        const toStage = CUSTOMER_STAGES[currentStep - 1].id;
        moveCustomer(customer, fromStage, toStage);
    };

    const moveCustomerToRejected = (customer) => {
        const fromStage = CUSTOMER_STAGES[currentStep].id;
        const toStage = 'rejected';
        moveCustomer(customer, fromStage, toStage);
    };

    // 고객 삭제
    const deleteCustomer = async () => {
        const { customer } = deleteModal;
        if (!customer) return;

        let currentStage = '';
        Object.keys(customers).forEach(stage => {
            if (customers[stage].some(c => c.id === customer.id)) {
                currentStage = stage;
            }
        });

        try {
            // IndexedDB에서 고객 삭제
            await deleteCustomerFromIdb(customer.id);

            await sendActionLog('고객_삭제', {
                customerName: customer.name,
                stage: CUSTOMER_STAGES.find(s => s.id === currentStage)?.title || currentStage
            });

            // GA4: 고객 삭제 이벤트
            try {
                const gtag = window.gtag;
                const measurementId = window.GA_MEASUREMENT_ID;
                if (typeof gtag === 'function' && measurementId) {
                    const deviceId = generateDeviceId();
                    gtag('event', 'customer_delete', {
                        customer_id: customer.id,
                        stage: currentStage,
                        priority: customer.priority || 'medium',
                        value: Number(customer.value) || 0,
                        device_id: deviceId
                    });
                }
            } catch (_) { }

            alert(`${customer.name} 고객이 성공적으로 삭제되었습니다.`);
        } catch (error) {
            console.error('고객 삭제 실패:', error);
            alert('고객 삭제 중 오류가 발생했습니다.');
        }

        setDeleteModal({ show: false, customer: null });
    };

    // 단계별 이동 처리
    const handleMoveCustomer = (targetStage) => {
        const { customer } = moveModal;
        if (!customer || !targetStage) return;

        let currentStage = '';
        Object.keys(customers).forEach(stage => {
            if (customers[stage].some(c => c.id === customer.id)) {
                currentStage = stage;
            }
        });

        if (currentStage && currentStage !== targetStage) {
            const fromTitle = (CUSTOMER_STAGES.find(s => s.id === currentStage) || {}).title || currentStage;
            const toTitle = (CUSTOMER_STAGES.find(s => s.id === targetStage) || {}).title || targetStage;
            moveCustomer(customer, currentStage, targetStage);
            try { alert(`${customer.name}님을 '${fromTitle}' → '${toTitle}' 단계로 이동했습니다.`); } catch (_) { }

            // 지도 모달이 열려있으면 마커 업데이트
            try {
                window.dispatchEvent(new CustomEvent('refreshKakaoMapMarkers'));
            } catch (e) { }
        }

        setMoveModal({ show: false, customer: null });
    };

    const handleBulkMove = (customersToMove) => {
        setBulkMoveModal({
            show: true,
            customers: customersToMove
        });
    };

    const confirmBulkMove = async (targetStage) => {
        const customersToMove = bulkMoveModal.customers;
        if (!customersToMove || customersToMove.length === 0) return;

        const newCustomers = { ...customers };
        const customerIds = new Set(customersToMove.map(c => c.id));
        const updatedCustomersList = [];

        // 1. Remove from original stages and collect updated customers
        Object.keys(newCustomers).forEach(stage => {
            if (newCustomers[stage]) {
                newCustomers[stage] = newCustomers[stage].filter(c => !customerIds.has(c.id));
            }
        });

        // 2. Add to target stage
        customersToMove.forEach(c => {
            const updated = { ...c, stage: targetStage };
            updatedCustomersList.push(updated);
        });

        // Add to target stage array
        newCustomers[targetStage] = [...(newCustomers[targetStage] || []), ...updatedCustomersList];

        // 3. Update UI state once
        setCustomers(newCustomers);
        setBulkMoveModal({ show: false, customers: [] });

        // 4. Persist and Log
        let successCount = 0;
        const promises = updatedCustomersList.map(async (customer) => {
            try {
                await idbPutCustomer(customer);

                // Logging
                // Re-find old stage from original object 'c'
                const original = customersToMove.find(o => o.id === customer.id);
                const originalStage = original?.__stageId || original?.stage || 'unknown';

                await sendActionLog('고객_단계_이동', {
                    customerName: customer.name,
                    fromStage: CUSTOMER_STAGES.find(s => s.id === originalStage)?.title || originalStage,
                    toStage: CUSTOMER_STAGES.find(s => s.id === targetStage)?.title || targetStage,
                    direction: 'bulk'
                });

                // GA4
                try {
                    const gtag = window.gtag;
                    const measurementId = window.GA_MEASUREMENT_ID;
                    if (typeof gtag === 'function' && measurementId) {
                        const deviceId = generateDeviceId();
                        gtag('event', 'stage_change', {
                            customer_id: customer.id,
                            from_stage: originalStage,
                            to_stage: targetStage,
                            priority: customer.priority || 'medium',
                            value: Number(customer.value) || 0,
                            device_id: deviceId,
                            method: 'bulk'
                        });
                    }
                } catch (_) { }

                return true;
            } catch (e) {
                console.error(`Failed to move customer ${customer.id}`, e);
                return false;
            }
        });

        const results = await Promise.all(promises);
        successCount = results.filter(Boolean).length;

        if (successCount > 0) {
            alert(`${successCount}명의 고객을 이동했습니다.`);
             // Refresh map if open
             try {
                window.dispatchEvent(new CustomEvent('refreshKakaoMapMarkers'));
            } catch (e) { }
        }
    };

    // showConfirmDialog 미사용


    // 스크롤 투 탑 함수
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleConfirmAction = () => {
        if (confirmModal.action === 'forward') {
            moveCustomerForward(confirmModal.customer);
        } else if (confirmModal.action === 'back') {
            moveCustomerBack(confirmModal.customer);
        } else if (confirmModal.action === 'reject') {
            moveCustomerToRejected(confirmModal.customer);
        } else if (confirmModal.action === 'exit_app') {
            // 앱 종료 허용: popstate 리스너 해제 후 뒤로 이동 호출
            try { window.__fcfunnel_popstate_cleanup__ && window.__fcfunnel_popstate_cleanup__(); } catch (_) { }
            setConfirmModal({ show: false, action: null, customer: null, message: '' });
            try { window.history.back(); } catch (_) { }
            // 일부 환경에서 한 번 더 필요할 수 있어 0ms 지연으로 한 번 더 시도
            setTimeout(() => { try { window.history.back(); } catch (_) { } }, 0);
            return;
        }
        setConfirmModal({ show: false, action: null, customer: null, message: '' });
    };

    // 카카오톡 인앱 브라우저 여부 확인
    const isKakaoBrowser = isKakaoInAppBrowser();
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const recommendedBrowser = isIOS ? 'Safari' : 'Chrome';
    const url = 'www.fcfunnel.com';
    const fullUrl = `https://${url}`;



    // 배너 높이 측정을 위한 ref
    const kakaoBannerRef = useRef(null);
    const headerRef = useRef(null);
    const [bannerHeight, setBannerHeight] = useState(0);

    // 배너 높이 측정 및 업데이트
    useEffect(() => {
        const updateBannerHeight = () => {
            let height = 0;
            if (isKakaoBrowser && kakaoBannerRef.current) {
                height = kakaoBannerRef.current.offsetHeight || 0;
            }
            // 배너가 존재하지 않거나 숨겨진 경우에도 0으로 재설정하여 상단 공백 방지
            setBannerHeight(height);
        };

        // 배너가 렌더링된 후 측정 (약간의 지연)
        const timer = setTimeout(() => {
            updateBannerHeight();
        }, 100);

        // ResizeObserver로 배너 크기 변경 감지
        const resizeObserver = new ResizeObserver(() => {
            updateBannerHeight();
        });

        if (isKakaoBrowser && kakaoBannerRef.current) {
            resizeObserver.observe(kakaoBannerRef.current);
        }

        // 리사이즈 이벤트 리스너 (폴백)
        window.addEventListener('resize', updateBannerHeight);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateBannerHeight);
            // 효과 종료 시 배너 높이를 초기화하여 잔여 여백을 제거
            setBannerHeight(0);
        };
    }, [isKakaoBrowser]);




    // 외부 브라우저로 열기 함수
    const handleOpenInExternalBrowser = () => {
        if (!isIOS) {
            // 안드로이드: 크롬으로 열기
            try {
                const intentUrl = `intent://${url}/#Intent;scheme=https;package=com.android.chrome;end`;
                window.location.href = intentUrl;
                setTimeout(() => {
                    window.location.href = fullUrl;
                }, 1000);
            } catch (_) {
                window.location.href = fullUrl;
            }
        } else {
            // iOS: Safari로 열기 (카카오톡 외부 브라우저 오픈 스킴 우선)
            try {
                const openExternal = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(fullUrl);
                window.location.href = openExternal;
                setTimeout(() => {
                    window.location.href = fullUrl;
                }, 800);
            } catch (_) {
                window.location.href = fullUrl;
            }
        }
    };



    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* 카카오톡 인앱 브라우저 배너 */}
            {isKakaoBrowser && (
                <motion.div
                    ref={kakaoBannerRef}
                    className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg"
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <div className="text-2xl">⚠️</div>
                            <div>
                                <div className="font-bold text-sm sm:text-base">카카오톡 인앱 브라우저</div>
                                <div className="text-xs sm:text-sm opacity-90">{recommendedBrowser}에서 더 나은 경험을 제공합니다</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleOpenInExternalBrowser}
                                className="px-4 py-2 bg-white text-red-600 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                            >
                                {recommendedBrowser}로 열기
                            </button>
                            <button
                                onClick={() => setShowBrowserModal(true)}
                                className="px-4 py-2 bg-[#FEE500] text-gray-800 rounded-lg font-bold text-sm hover:bg-[#FEE500]/90 transition-colors whitespace-nowrap"
                            >
                                안내보기
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}



            {/* 배너가 fixed로 떠 있을 때 레이아웃을 자연스럽게 밀어주는 스페이서 */}
            {bannerHeight > 0 && (
                <div aria-hidden="true" style={{ height: `${bannerHeight}px` }} />
            )}

            {/* 모던 헤더 */}
            <div
                ref={headerRef}
                className="bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-lg sticky z-40"
                style={{
                    top: bannerHeight > 0 ? `${bannerHeight}px` : '0px'
                }}
            >
                <div className="px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            {/* 모던 메뉴 버튼 */}
                            <motion.button
                                onClick={() => setShowSideMenu(true)}
                                className="group relative p-3 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50"
                                title="메뉴"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <Menu className="w-5 h-5 text-gray-700 group-hover:text-blue-600 transition-colors duration-300" />
                            </motion.button>

                            {/* 브랜드 영역 */}
                            <div className="flex items-center gap-3">
                                <div>
                                    <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                                        FC퍼널
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* 상태 표시 - 원형 컬러 버튼 */}
                            {(appStatus === 'trial' || (appStatus === 'pending' && trialInfo && trialInfo.status === 'trial')) && (
                                <button
                                    onClick={() => {
                                        // GA4: 사용신청 버튼 클릭 이벤트 추적
                                        try {
                                            const gtag = window.gtag;
                                            const measurementId = window.GA_MEASUREMENT_ID;
                                            if (typeof gtag === 'function' && measurementId) {
                                                const deviceId = generateDeviceId();
                                                gtag('event', 'application_button_click', {
                                                    app_status: appStatus,
                                                    button_location: 'customer_management_header',
                                                    event_category: 'application',
                                                    event_label: '사용신청 버튼 클릭',
                                                    device_id: deviceId
                                                });
                                            }
                                        } catch (gaError) {
                                            console.warn('GA4 이벤트 전송 실패:', gaError);
                                        }

                                        try {
                                            window.dispatchEvent(new Event('openApprovalModal'));
                                        } catch (e) {
                                            console.warn('[TrialButton] dispatch failed', e);
                                        }
                                    }}
                                    className="w-9 h-9 rounded-full bg-red-500 shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-105 transition-all"
                                    title={(() => {
                                        try {
                                            // trialInfo에서 daysRemaining을 우선 사용, 없으면 localStorage의 trialStartDate 사용
                                            let daysLeft = null;

                                            if (trialInfo && typeof trialInfo.daysRemaining === 'number') {
                                                daysLeft = trialInfo.daysRemaining;
                                            } else {
                                                // 공통 유틸리티 함수 사용
                                                const trialStartDate = localStorage.getItem('trialStartDate');
                                                if (trialStartDate) {
                                                    daysLeft = calculateRemainingDays(trialStartDate);
                                                } else {
                                                    // 폴백: verificationDate 사용 (기존 호환성)
                                                    const v = localStorage.getItem('verificationDate');
                                                    if (v) {
                                                        daysLeft = calculateRemainingDays(v);
                                                    }
                                                }
                                            }

                                            if (daysLeft === null) return '체험버전';
                                            return `체험버전 · ${daysLeft}일 남음`;
                                        } catch (_) { return '체험버전'; }
                                    })()}
                                    aria-label="체험버전"
                                />
                            )}
                            {appStatus === 'active' && (
                                <button
                                    onClick={() => {
                                        if (nextBillingDate) {
                                            const d = new Date(nextBillingDate);
                                            alert(`다음 결제일: ${d.toLocaleDateString('ko-KR')}`);
                                        } else {
                                            alert('정식 버전');
                                        }
                                    }}
                                    className="w-9 h-9 rounded-full bg-green-600 shadow-lg shadow-green-600/25 hover:shadow-green-600/40 hover:scale-105 transition-all"
                                    title="정식 버전"
                                    aria-label="정식 버전"
                                />
                            )}

                            {/* 공지사항 버튼 */}
                            <motion.button
                                onClick={() => {
                                    sendActionLog('AI비서_열기');

                                    // GA4: AI 비서 열기 이벤트
                                    try {
                                        const gtag = window.gtag;
                                        const measurementId = window.GA_MEASUREMENT_ID;
                                        if (typeof gtag === 'function' && measurementId) {
                                            const deviceId = generateDeviceId();
                                            gtag('event', 'assistant_open', {
                                                feature_name: 'ai_assistant',
                                                method: 'button_click',
                                                badge_count: aiBadgeCount || 0,
                                                device_id: deviceId
                                            });
                                        }
                                    } catch (_) { }

                                    setShowAssistant(true);
                                }}
                                className="group relative p-3 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl text-white shadow-lg shadow-orange-500/25 hover:shadow-xl transition-all duration-300"
                                title="AI 비서"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <Bell className="w-5 h-5 group-hover:animate-pulse" />
                                {aiBadgeCount > 0 && (
                                    <motion.span
                                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: "spring", stiffness: 500, damping: 15 }}
                                    >
                                        {aiBadgeCount > 9 ? '9+' : aiBadgeCount}
                                    </motion.span>
                                )}
                            </motion.button>

                            {/* 도움말 버튼 */}
                            <motion.button
                                onClick={() => {
                                    sendActionLog('지도_열기');

                                    // GA4: 지도 열기 이벤트
                                    try {
                                        const gtag = window.gtag;
                                        const measurementId = window.GA_MEASUREMENT_ID;
                                        if (typeof gtag === 'function' && measurementId) {
                                            const deviceId = generateDeviceId();
                                            gtag('event', 'map_open', {
                                                feature_name: 'kakao_map',
                                                method: 'button_click',
                                                device_id: deviceId
                                            });
                                        }
                                    } catch (_) { }

                                    setShowKakaoMap(true);
                                    try { window.dispatchEvent(new Event('kakaoMapOpen')); } catch (_) { }
                                }}
                                className="group relative p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white shadow-lg shadow-blue-500/25 hover:shadow-xl transition-all duration-300"
                                title="지도 열기"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <MapIcon className="w-5 h-5" />
                            </motion.button>

                            {/* 공유 프로모션 버튼 */}
                            {/* <motion.button
                                onClick={() => {
                                    sendActionLog('공유프로모션_열기');
                                    if (isMobile()) {
                                        // 카카오톡 인앱 브라우저인 경우 브라우저 호환성 모달 표시
                                        if (isKakaoInAppBrowser()) {
                                            setShowShareBrowserModal(true);
                                        } else {
                                            navigate('/share-promotion');
                                        }
                                    } else {
                                        alert('앱 공유 프로모션은 모바일 기기에서만 이용 가능합니다.');
                                    }
                                }}
                                className="group relative p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white shadow-lg shadow-green-500/25 hover:shadow-xl transition-all duration-300"
                                title="앱 공유하기"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <Share2 className="w-5 h-5" />
                            </motion.button> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* 모던 사이드바 메뉴 */}
            <AnimatePresence>
                {showSideMenu && (
                    <motion.div
                        className="fixed inset-0 z-40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {/* 배경 오버레이 */}
                        <motion.div
                            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                            onClick={() => setShowSideMenu(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        ></motion.div>

                        {/* 사이드바 */}
                        <motion.div
                            className="absolute left-0 top-0 h-full w-80 bg-white/95 backdrop-blur-xl shadow-2xl border-r border-white/20"
                            variants={sidebarVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="p-6 h-full overflow-y-auto">
                                {/* 사이드바 헤더 */}
                                <div className="flex justify-between items-center mb-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                                            <Settings className="w-5 h-5 text-white" />
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-800">메뉴</h2>
                                    </div>
                                    <motion.button
                                        onClick={() => setShowSideMenu(false)}
                                        className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-300"
                                        variants={buttonVariants}
                                        whileHover="hover"
                                        whileTap="tap"
                                    >
                                        <X className="w-5 h-5" />
                                    </motion.button>
                                </div>


                                {/* 메뉴 항목들 */}
                                <div className="space-y-3">
                                    <div className="border-t border-gray-200 pt-4">
                                        <button
                                            onClick={() => {
                                                setCurrentStep(0);
                                                handleShowAddForm(CUSTOMER_STAGES[0].id);
                                                setShowSideMenu(false);
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg shadow-green-500/25 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                                                신규 고객 추가
                                            </div>
                                        </button>
                                    </div>

                                    <div className="border-t border-gray-200 pt-4 space-y-3">
                                        {/* 단체 카카오톡 발송 버튼 */}
                                        <button
                                            onClick={() => {
                                                try {
                                                    const ua = navigator.userAgent || navigator.vendor || window.opera;
                                                    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
                                                    if (!isMobile) {
                                                        try { alert('모바일에서만 동작하는 기능입니다'); } catch (_) { }
                                                        return;
                                                    }
                                                } catch (_) { }
                                                sendActionLog('단체카카오톡_모달열기');
                                                setShowBulkKakaoModal(true);
                                                setBulkKakaoMessage('');
                                                setShowSideMenu(false);
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-lg shadow-yellow-500/25 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Users className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                                                단체 카카오톡 발송
                                            </div>
                                        </button>

                                        {/* 보고서 생성 버튼 - 주석처리 */}
                                        {/* <button
                                            onClick={() => {
                                                sendActionLog('리포트_생성');
                                                setShowReportGenerator(true);
                                                setShowSideMenu(false);
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-md transition-all duration-300 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FileText className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300 text-green-500" />
                                                보고서 생성
                                            </div>
                                        </button> */}

                                        {/* 엑셀파일 다운받기 (고객/히스토리 시트 포함) */}
                                        <button
                                            onClick={async () => {
                                                try {
                                                    sendActionLog('CSV_내보내기');
                                                    await exportCustomersCsvWithSections(customers);
                                                } catch (e) {
                                                    console.error('엑셀 내보내기 실패:', e);
                                                    alert('엑셀 내보내기 중 오류가 발생했습니다.');
                                                } finally {
                                                    setShowSideMenu(false);
                                                }
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-md transition-all duration-300 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Download className="w-5 h-5 group-hover:scale-110 transition-transform duration-300 text-blue-600" />
                                                엑셀파일 다운로드
                                            </div>
                                        </button>






                                        {/* 오락실 버튼은 공지사항 아래에 메인 기능으로 배치됨 */}

                                        <button
                                            onClick={() => {
                                                sendActionLog('공지사항_보기');
                                                setShowNotice(true);
                                                setShowSideMenu(false);
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-md transition-all duration-300 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Bell className="w-5 h-5 group-hover:animate-pulse text-orange-500" />
                                                공지사항
                                            </div>
                                        </button>


                                        {/* 설정 버튼 */}
                                        <button
                                            onClick={() => {
                                                sendActionLog('설정_열기');
                                                setShowSettingsModal(true);
                                                setShowSideMenu(false);
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-md transition-all duration-300 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Settings className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                                                설정
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => {
                                                setShowHelpModal(true);
                                                setShowSideMenu(false);
                                            }}
                                            className="w-full text-left p-4 rounded-2xl font-bold bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-md transition-all duration-300 group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <HelpCircle className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
                                                도움말
                                            </div>
                                        </button>

                                        {/* 기능개선요청 */}


                                        {/* 문의하기 - 카카오채널 */}
                                        <a
                                            href="http://pf.kakao.com/_XTTxoxj/chat"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block w-full text-left p-4 rounded-2xl font-bold bg-[#FEE500]/10 text-gray-800 hover:bg-[#FEE500]/20 hover:shadow-md transition-all duration-300 group"
                                            onClick={() => setShowSideMenu(false)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <MessageCircle className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300 text-[#FEE500]" />
                                                카톡문의
                                            </div>
                                        </a>

                                        {/* 회사소개 */}
                                        <a
                                            href="https://previaworks.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block w-full text-left p-4 rounded-2xl font-bold bg-blue-50 text-gray-800 hover:bg-blue-100 hover:shadow-md transition-all duration-300 group"
                                            onClick={() => setShowSideMenu(false)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <Building2 className="w-5 h-5 group-hover:scale-110 transition-transform duration-300 text-blue-600" />
                                                회사소개
                                            </div>
                                        </a>



                                    </div>

                                    {/* 앱 정보 */}
                                    <div className="border-t border-gray-200 pt-6">
                                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                                            <div className="text-sm text-gray-600 space-y-2">
                                                <div className="font-bold text-gray-800 mb-3">앱 정보</div>
                                                <div
                                                    className="flex items-center justify-between cursor-pointer hover:opacity-70 transition-opacity"
                                                    onClick={() => {
                                                        sendActionLog('오락실_열기');
                                                        setShowArcade(true);
                                                        setShowSideMenu(false);
                                                    }}
                                                    onTouchStart={() => {
                                                        sendActionLog('오락실_열기');
                                                        setShowArcade(true);
                                                        setShowSideMenu(false);
                                                    }}
                                                >
                                                    <span>버전</span>
                                                    <span className="font-medium">1.96.0</span>
                                                </div>
                                                {/* 사용자 이름 표시 (정식버전일 때만) */}
                                                {appStatus === 'active' && userName && (
                                                    <div className="flex items-center justify-between">
                                                        <span>사용자</span>
                                                        <span className="font-medium text-blue-600">{userName}</span>
                                                    </div>
                                                )}
                                                <div className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-300">
                                                    <div className="flex items-center justify-between">
                                                        <span>Device ID: {generateDeviceId().slice(0, 8)}</span>
                                                        {deviceLinkInfo && (
                                                            <span className={`px-2 py-1 rounded text-xs font-medium ${deviceLinkInfo.isSecondary
                                                                ? 'bg-blue-50 text-blue-700'
                                                                : 'bg-green-50 text-green-700'
                                                                }`}>
                                                                {deviceLinkInfo.isSecondary ? '추가기기' : '본 기기'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-gray-600 mt-2">
                                                    <span>PWA 지원</span>
                                                    <span className="font-medium text-green-600">✓</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 서비스 이용약관 및 개인정보처리방침 */}
                                    <div className="border-t border-gray-200 pt-4">
                                        <div className="text-center space-y-2">
                                            <button
                                                onClick={() => {
                                                    setShowImportModal(true);
                                                    setShowSideMenu(false);
                                                }}
                                                className="text-xs text-gray-600 hover:text-blue-700 transition-colors duration-200 underline block"
                                            >
                                                영업의신세계 데이터
                                            </button>
                                            <button
                                                onClick={() => {
                                                    sendActionLog('고객일괄입력_모달열기');
                                                    setShowBulkCustomerInputModal(true);
                                                    setShowSideMenu(false);
                                                }}
                                                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200 underline block"
                                            >
                                                고객일괄입력
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    // 다중 확인 절차
                                                    const step1 = window.confirm('⚠️ 데이터 초기화: 모든 고객 데이터가 삭제됩니다. 계속하시겠습니까?');
                                                    if (!step1) return;
                                                    const step2 = window.prompt('정말 진행하시려면 다음 문장을 정확히 입력하세요: 삭제합니다');
                                                    if (step2 !== '삭제합니다') {
                                                        alert('문구가 일치하지 않아 취소되었습니다.');
                                                        return;
                                                    }
                                                    const step3 = window.confirm('마지막 확인: 되돌릴 수 없습니다. 진행하시겠습니까?');
                                                    if (!step3) return;

                                                    try {
                                                        // IndexedDB 데이터 완전 초기화
                                                        const { clearAllData } = await import('./utils/idb');
                                                        await clearAllData();

                                                        // 상태 초기화
                                                        const empty = { prospect: [], preparation: [], appointment: [], consultation: [], closed: [], rejected: [], bank: [] };
                                                        setCustomers(empty);

                                                        alert('✅ 모든 고객 데이터가 완전히 초기화되었습니다.');
                                                    } catch (e) {
                                                        console.error('데이터 초기화 실패:', e);
                                                        alert('❌ 데이터 초기화 중 오류가 발생했습니다.');
                                                    }
                                                }}
                                                className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200 underline block"
                                            >
                                                데이터 초기화
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowTermsModal(true);
                                                    setShowSideMenu(false);
                                                }}
                                                className="text-xs text-gray-500 hover:text-blue-600 transition-colors duration-200 underline block"
                                            >
                                                서비스 이용약관
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowPrivacyModal(true);
                                                    setShowSideMenu(false);
                                                }}
                                                className="text-xs text-gray-500 hover:text-green-600 transition-colors duration-200 underline block"
                                            >
                                                개인정보처리방침
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* 초간편고객관리 프로모션 - 정식 활성 사용자에게는 노출하지 않음 */}
            {appStatus !== DEVICE_STATUS.ACTIVE && appStatus !== 'active' && (
                <div className="px-4 sm:px-6 pt-4">
                    <a
                        href="https://play.google.com/store/apps/details?id=com.previaworks.soeasycrm"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block max-w-6xl mx-auto overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-md hover:shadow-lg transition-all duration-300"
                        aria-label="초간편고객관리 Google Play에서 보기"
                    >
                        <div className="grid grid-cols-[96px_1fr] sm:grid-cols-[180px_1fr] items-stretch min-h-[132px] sm:min-h-[168px]">
                            <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-cyan-50 to-white flex items-center justify-center">
                                <div className="absolute -top-8 -left-8 w-24 h-24 rounded-full bg-emerald-100/70" aria-hidden="true" />
                                <div className="absolute -bottom-10 -right-10 w-28 h-28 rounded-full bg-cyan-100/70" aria-hidden="true" />
                                <div className="relative w-14 sm:w-24 h-24 sm:h-36 rounded-[18px] sm:rounded-[26px] border-[3px] sm:border-4 border-slate-800 bg-white shadow-xl rotate-[-5deg] group-hover:rotate-[-2deg] transition-transform duration-300 p-1.5 sm:p-2">
                                    <div className="h-full rounded-[12px] sm:rounded-[18px] bg-slate-50 overflow-hidden">
                                        <div className="h-4 sm:h-5 bg-white border-b border-slate-100" />
                                        <div className="px-1.5 sm:px-2 pt-2 space-y-1.5">
                                            <div className="h-1.5 sm:h-2 w-9 sm:w-14 bg-emerald-500 rounded-full" />
                                            <div className="h-1 sm:h-1.5 w-8 sm:w-12 bg-slate-300 rounded-full" />
                                            <div className="mt-2 grid grid-cols-2 gap-1">
                                                <div className="h-5 sm:h-8 rounded bg-emerald-50 border border-emerald-100" />
                                                <div className="h-5 sm:h-8 rounded bg-cyan-50 border border-cyan-100" />
                                            </div>
                                            <div className="h-2 sm:h-3 rounded bg-white border border-slate-100" />
                                            <div className="h-2 sm:h-3 rounded bg-white border border-slate-100" />
                                            <div className="h-2 sm:h-3 rounded bg-white border border-slate-100" />
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute right-1.5 bottom-2 sm:right-3 sm:bottom-3 rounded-full bg-emerald-600 text-white text-[9px] sm:text-xs font-extrabold px-2 py-1 shadow-md">
                                    2.0
                                </div>
                            </div>
                            <div className="p-4 sm:p-6 flex flex-col justify-center">
                                <div className="text-xs sm:text-sm font-bold text-emerald-600 mb-1">무료 고객관리 앱</div>
                                <div className="text-lg sm:text-2xl font-extrabold text-slate-900 leading-tight">
                                    고객 관리, 더 단순하게.
                                    <span className="block text-emerald-600">초간편고객관리</span>
                                </div>
                                <div className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed">
                                    고객과 소통 기록을 한 곳에서 관리하고, 데이터는 내 기기에만 저장합니다.
                                </div>
                                <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700">
                                    Google Play에서 보기
                                    <span aria-hidden="true" className="group-hover:translate-x-1 transition-transform">→</span>
                                </div>
                            </div>
                        </div>
                    </a>
                </div>
            )}

            {/* 메인 컨텐츠 - 상단 여백 제거 (sticky 헤더는 문서 흐름에 공간을 차지하므로 패딩 불필요) */}
            <div>
                <CustomerOverview
                    customers={customers}
                    onShowAddForm={handleShowAddForm}
                    onEditCustomer={handleEditCustomer}
                    onDeleteCustomer={(customer) => setDeleteModal({ show: true, customer })}
                    onMoveCustomer={(customer) => setMoveModal({ show: true, customer })}
                    onBulkMove={handleBulkMove}
                    onLoadMoreStage={async (stageId) => {
                        try {
                            const offset = (customers[stageId] || []).length;
                            const next = await idbGetCustomersByStage(stageId, offset, 50);
                            if (next && next.length > 0) {
                                const existingIds = new Set((customers[stageId] || []).map(c => c.id));
                                const merged = [
                                    ...(customers[stageId] || []),
                                    ...next.filter(c => !existingIds.has(c.id))
                                ].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                                const updated = { ...customers, [stageId]: merged };
                                setCustomers(updated);
                                // IndexedDB는 자동으로 저장되므로 별도 저장 불필요
                            }
                        } catch (_) { /* noop */ }
                    }}
                    setCurrentStep={setCurrentStep}
                    appStatus={appStatus}
                    trialInfo={trialInfo}
                    showListView={showListView}
                    businessType={businessType}
                />
            </div>

            {/* 고객 추가/수정 폼 */}
            {showAddForm && (
                <CustomerForm
                    editingCustomer={editingCustomer}
                    targetStageForAdd={targetStageForAdd}
                    customers={customers}
                    setCustomers={setCustomers}
                    addCustomer={addCustomer}
                    updateCustomer={updateCustomer}
                    sendActionLog={sendActionLog}
                    appStatus={appStatus}
                    onClose={handleCloseForm}
                />
            )}

            {/* 모달들 */}
            {showHelpModal && (
                <HelpModal onClose={() => setShowHelpModal(false)} />
            )}

            {/* 지도 모달 */}
            <KakaoMapModal
                isOpen={showKakaoMap}
                onClose={() => { setShowKakaoMap(false); try { window.dispatchEvent(new Event('kakaoMapClose')); } catch (_) { } }}
            />

            {showBrowserModal && (
                <BrowserModal onClose={() => setShowBrowserModal(false)} />
            )}

            <ShareBrowserModal
                isOpen={showShareBrowserModal}
                onClose={() => setShowShareBrowserModal(false)}
            />

            {showNotice && (
                <Notice onClose={handleNoticeClose} />
            )}

            {/* AI 비서 모달 */}
            {showAssistant && (
                <AssistantModal
                    customers={customers}
                    onSelectCustomer={(customer) => {
                        // CustomerOverview처럼 상세 오버레이를 띄우기 위해 커스텀 이벤트 디스패치
                        try {
                            window.dispatchEvent(new CustomEvent('openCustomerOverlayFromAssistant', { detail: { customer } }));
                        } catch (e) { }
                    }}
                    onClose={() => setShowAssistant(false)}
                />
            )}

            {showReportGenerator && (
                <ReportGenerator
                    customers={customers}
                    onClose={() => setShowReportGenerator(false)}
                />
            )}



            {/* 알림센터 모달 추가 */}



            <ConfirmModal
                show={confirmModal.show}
                message={confirmModal.message}
                action={confirmModal.action}
                onConfirm={handleConfirmAction}
                onCancel={() => setConfirmModal({ show: false, action: null, customer: null, message: '' })}
            />

            <DeleteModal
                show={deleteModal.show}
                customer={deleteModal.customer}
                onConfirm={deleteCustomer}
                onCancel={() => setDeleteModal({ show: false, customer: null })}
            />

            <MoveModal
                show={moveModal.show}
                customer={moveModal.customer}
                customers={customers}
                onConfirm={handleMoveCustomer}
                onCancel={() => setMoveModal({ show: false, customer: null })}
            />

            <BulkMoveModal
                show={bulkMoveModal.show}
                customers={bulkMoveModal.customers}
                businessType={businessType}
                onConfirm={confirmBulkMove}
                onCancel={() => setBulkMoveModal({ show: false, customers: [] })}
            />

            {/* 서비스 이용약관 모달 */}
            {showTermsModal && (
                <TermsOfServiceModal onClose={() => setShowTermsModal(false)} />
            )}

            {/* 개인정보처리방침 모달 */}
            {showPrivacyModal && (
                <PrivacyPolicyModal onClose={() => setShowPrivacyModal(false)} />
            )}

            {/* 레거시 데이터 임포트 모달 */}
            {showImportModal && (
                <ImportLegacyModal
                    show={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    onImported={() => {
                        // IndexedDB에서 데이터를 다시 로드
                        reloadCustomers();
                        setShowImportModal(false);
                    }}
                />
            )}

            {/* 설정 모달 */}
            <SettingsModal
                isOpen={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                appStatus={appStatus}
            />

            {/* 고객 일괄 입력 모달 */}
            <BulkCustomerInputModal
                show={showBulkCustomerInputModal}
                onClose={() => setShowBulkCustomerInputModal(false)}
                onSave={async (customerList) => {
                    try {
                        // 체험 종료 상태일 때 누적 고객 등록수 제한 체크
                        if (appStatus === 'expired') {
                            const { getExpiredCustomerCount } = await import('./utils/expiredActionLimit');
                            const currentCount = await getExpiredCustomerCount();
                            const remainingSlots = 10 - currentCount;

                            if (remainingSlots <= 0) {
                                alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${currentCount}명 등록되어 있습니다.\n계속 사용하려면 사용신청을 해주세요.`);
                                try {
                                    window.dispatchEvent(new Event('openApprovalModal'));
                                } catch (_) { }
                                return;
                            }

                            if (customerList.length > remainingSlots) {
                                alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${currentCount}명 등록되어 있으며, ${remainingSlots}명만 추가로 등록할 수 있습니다.`);
                                return;
                            }
                        }

                        let successCount = 0;
                        let failCount = 0;

                        for (const customerData of customerList) {
                            try {
                                // 체험 종료 상태일 때 각 고객 추가 시마다 체크
                                if (appStatus === 'expired') {
                                    const { tryAddExpiredCustomer, getExpiredCustomerCount } = await import('./utils/expiredActionLimit');
                                    const handleLimitExceeded = async () => {
                                        const count = await getExpiredCustomerCount();
                                        alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${count}명 등록되어 있습니다.\n계속 사용하려면 사용신청을 해주세요.`);
                                        try {
                                            window.dispatchEvent(new Event('openApprovalModal'));
                                        } catch (_) { }
                                    };

                                    const canAdd = await tryAddExpiredCustomer(null, handleLimitExceeded);
                                    if (!canAdd) {
                                        failCount++;
                                        continue;
                                    }
                                }

                                const customer = {
                                    id: crypto.randomUUID(),
                                    name: customerData.name,
                                    phone: customerData.phone,
                                    stage: 'prospect',
                                    createdAt: new Date().toISOString()
                                };

                                await addCustomer(customer);

                                // 히스토리 초기화
                                try {
                                    const { ensureHistoryInitialized } = await import('./hooks/useCustomerHistory');
                                    await ensureHistoryInitialized(customer.id);
                                } catch (_) { }

                                // 활동 로그 기록
                                try {
                                    await sendActionLog('고객_추가', {
                                        source: 'bulk_input',
                                        customerName: customer.name,
                                        targetStage: '가망고객'
                                    });
                                } catch (_) { }

                                successCount++;
                            } catch (error) {
                                console.error(`고객 등록 실패 (${customerData.name}):`, error);
                                failCount++;
                            }
                        }

                        if (successCount > 0) {
                            alert(`✅ ${successCount}명의 고객이 등록되었습니다.${failCount > 0 ? `\n⚠️ ${failCount}명의 고객 등록에 실패했습니다.` : ''}`);
                        } else {
                            alert('❌ 고객 등록에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('고객 일괄 등록 실패:', error);
                        alert('고객 등록 중 오류가 발생했습니다.');
                    }
                }}
            />

            {/* 데이터 마이그레이션 알림 */}
            {showMigrationAlert && (
                <DataMigrationAlert
                    onClose={() => setShowMigrationAlert(false)}
                    onRetry={() => {
                        setShowMigrationAlert(false);
                        window.location.reload();
                    }}
                />
            )}

            {/* 오락실 모달: 사이드바 밖, 앱 루트 레벨에서 전체화면 */}
            <ArcadeModal isOpen={showArcade} onClose={() => setShowArcade(false)} />

            {/* 환영 모달 */}
            {showWelcomeModal && (
                <WelcomeModal onClose={() => {
                    setShowWelcomeModal(false);
                    localStorage.setItem('welcome_modal_shown', '1');
                }} />
            )}

            {/* 체험 사용자 고객 등록 유도 모달 */}
            {showTrialRegistrationModal && (
                <TrialCustomerRegistrationModal
                    onClose={() => {
                        setShowTrialRegistrationModal(false);
                        // 오늘 날짜 저장
                        const now = new Date();
                        const kstTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
                        const yyyy = String(kstTime.getFullYear());
                        const mm = String(kstTime.getMonth() + 1).padStart(2, '0');
                        const dd = String(kstTime.getDate()).padStart(2, '0');
                        const today = `${yyyy}-${mm}-${dd}`;
                        localStorage.setItem('trialRegistrationModalLastShown', today);
                    }}
                    onRegister={() => {
                        // 고객 등록 폼 열기
                        handleShowAddForm(CUSTOMER_STAGES[0]?.id || null);
                    }}
                />
            )}

            {/* 다음 행동 모달 */}
            <NextActionModal
                isOpen={nextAction.open}
                onClose={() => setNextAction({ open: false, customer: null })}
                customer={nextAction.customer}
            />

            {/* 모던 푸터 */}
            <div className="bg-white/60 backdrop-blur-lg border-t border-white/20 p-4">
                <div className="text-center space-y-2">
                    <button
                        onClick={() => setShowEaster(true)}
                        className="text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded-full inline-block hover:bg-gray-200"
                        title="비밀 이스터에그"
                    >
                        Device ID: {generateDeviceId()}
                    </button>
                    <div className="text-xs text-gray-400">
                        ⓒ2025. PreviaWorks All rights reserved
                    </div>
                </div>
            </div>



            {/* 스크롤 투 탑 플로팅 버튼 */}
            <AnimatePresence>
                {showScrollToTop && !hideFloatingForMap && !((showKakaoMap) || (confirmModal && confirmModal.show) || (deleteModal && deleteModal.show) || (moveModal && moveModal.show) || (bulkMoveModal && bulkMoveModal.show) || showImportModal || showSettingsModal || showArcade || (nextAction && nextAction.open) || showEaster || showBulkKakaoModal || showAssistant) && (
                    <motion.button
                        onClick={scrollToTop}
                        className="fixed bottom-28 right-6 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 z-50 flex items-center justify-center"
                        title="맨 위로"
                        aria-label="맨 위로 스크롤"
                        variants={floatingButtonVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <ChevronUp className="w-6 h-6" />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* 리스트/그리드 전환 플로팅 버튼 */}
            <AnimatePresence>
                {!hideFloatingForMap && showListToggle && !((showKakaoMap) || (confirmModal && confirmModal.show) || (deleteModal && deleteModal.show) || (moveModal && moveModal.show) || (bulkMoveModal && bulkMoveModal.show) || showImportModal || showSettingsModal || showArcade || (nextAction && nextAction.open) || showEaster || showBulkKakaoModal || showAssistant) && (
                    <motion.button
                        onClick={() => setShowListView(v => !v)}
                        className="fixed bottom-44 right-6 w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center justify-center"
                        title={showListView ? '보드 뷰로 보기' : '리스트 뷰로 보기'}
                        aria-label={showListView ? '보드 뷰로 보기' : '리스트 뷰로 보기'}
                        variants={floatingButtonVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        {showListView ? <LayoutGrid className="w-6 h-6" /> : <List className="w-6 h-6" />}
                    </motion.button>
                )}
            </AnimatePresence>

            {/* 고객 추가 플로팅 버튼 (항상 표시) */}
            <AnimatePresence>
                {!hideFloatingForMap && !((showKakaoMap) || (confirmModal && confirmModal.show) || (deleteModal && deleteModal.show) || (moveModal && moveModal.show) || (bulkMoveModal && bulkMoveModal.show) || showImportModal || showSettingsModal || showArcade || (nextAction && nextAction.open) || showEaster || showBulkKakaoModal || showAssistant) && (
                    <motion.button
                        onClick={() => handleShowAddForm((CUSTOMER_STAGES[currentStep] || {}).id)}
                        className="fixed bottom-12 right-6 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center justify-center"
                        title="고객 추가"
                        aria-label="고객 추가"
                        variants={floatingButtonVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <Plus className="w-6 h-6" />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* 빠른 실행 패널 (비활성화) */}

            {/* (streak 토스트 제거) */}

            {/* 단체 카카오톡 발송 모달 */}
            {showBulkKakaoModal && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 1200 }} onClick={() => setShowBulkKakaoModal(false)}>
                    <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full shadow-2xl border border-white/20 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-xl bg-yellow-100 border border-yellow-200 flex items-center justify-center">
                                    <MessageCircle className="w-5 h-5 text-yellow-700" />
                                </div>
                                <h3 className="text-base font-bold text-gray-800">단체 카카오톡 발송</h3>
                            </div>
                            <button onClick={() => setShowBulkKakaoModal(false)} className="p-2 rounded-lg hover:bg-gray-100">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* 타입 선택 탭 */}
                        <div className="p-5 border-b border-gray-100">
                            <div className="flex bg-gray-100 rounded-xl p-1">
                                <button
                                    onClick={() => setBulkKakaoType('text')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg font-medium transition-all ${bulkKakaoType === 'text'
                                        ? 'bg-white text-gray-800 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    <Type className="w-4 h-4" />
                                    텍스트
                                </button>
                                <button
                                    onClick={() => setBulkKakaoType('image')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg font-medium transition-all ${bulkKakaoType === 'image'
                                        ? 'bg-white text-gray-800 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-800'
                                        }`}
                                >
                                    <Image className="w-4 h-4" />
                                    이미지
                                </button>
                            </div>
                        </div>

                        <div className="p-5 space-y-3">
                            {bulkKakaoType === 'text' ? (
                                <>
                                    <div className="text-sm text-gray-600">고객님들께 보낼 메시지를 입력하세요.</div>
                                    <textarea
                                        value={bulkKakaoMessage}
                                        onChange={(e) => setBulkKakaoMessage(e.target.value)}
                                        placeholder="메시지 내용을 입력하세요"
                                        rows="6"
                                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-yellow-500 transition-all duration-300 resize-y"
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="text-sm text-gray-600">고객님들께 보낼 이미지를 선택하세요.</div>
                                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                                        {bulkKakaoImage ? (
                                            <div className="space-y-3">
                                                <img
                                                    src={bulkKakaoImage}
                                                    alt="선택된 이미지"
                                                    className="max-h-48 mx-auto rounded-lg shadow-sm"
                                                />
                                                <button
                                                    onClick={() => {
                                                        setBulkKakaoImage(null);
                                                        setBulkKakaoImageFile(null);
                                                    }}
                                                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                                                >
                                                    이미지 제거
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center">
                                                    <Image className="w-6 h-6 text-gray-400" />
                                                </div>
                                                <div className="space-y-2">
                                                    <button
                                                        onClick={() => {
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.multiple = false;
                                                            input.onchange = (e) => {
                                                                const file = e.target.files[0];
                                                                if (file) {
                                                                    // 파일 크기 체크 (10MB 제한)
                                                                    if (file.size > 10 * 1024 * 1024) {
                                                                        alert('이미지 파일 크기는 10MB 이하여야 합니다.');
                                                                        return;
                                                                    }

                                                                    setBulkKakaoImageFile(file);
                                                                    const reader = new FileReader();
                                                                    reader.onload = (e) => setBulkKakaoImage(e.target.result);
                                                                    reader.readAsDataURL(file);
                                                                }
                                                            };
                                                            input.click();
                                                        }}
                                                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
                                                    >
                                                        📎 이미지 첨부
                                                    </button>
                                                    <p className="text-xs text-gray-500">또는 이미지를 여기에 드래그하세요</p>
                                                </div>
                                                <p className="text-xs text-gray-500">JPG, PNG, GIF 파일만 지원됩니다</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                💡 <strong>안내:</strong> '전송'버튼을 누르면 카카오톡 앱을 선택할 수 있습니다. 수신대상은 카톡에서 직접 설정하시면 됩니다.
                            </div>
                        </div>

                        <div className="p-5 border-t border-gray-100 flex gap-3">
                            <button
                                onClick={() => {
                                    setShowBulkKakaoModal(false);
                                    setBulkKakaoMessage('');
                                    setBulkKakaoImage(null);
                                    setBulkKakaoImageFile(null);
                                    setBulkKakaoType('text');
                                }}
                                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                            >
                                취소
                            </button>
                            <button
                                onClick={async () => {
                                    if (bulkKakaoType === 'text' && !bulkKakaoMessage.trim()) {
                                        alert('메시지를 입력해주세요.');
                                        return;
                                    }
                                    if (bulkKakaoType === 'image' && !bulkKakaoImage) {
                                        alert('이미지를 선택해주세요.');
                                        return;
                                    }

                                    // 카카오톡 앱 바로 열기
                                    try {
                                        // 단체 카카오톡 발송 로깅
                                        sendActionLog('단체카카오톡_발송', {
                                            messageType: bulkKakaoType,
                                            hasMessage: !!bulkKakaoMessage.trim(),
                                            hasImage: !!bulkKakaoImage
                                        });

                                        if (bulkKakaoType === 'text') {
                                            // 텍스트 메시지의 경우 Web Share API 시도
                                            if (navigator.share) {
                                                try {
                                                    await navigator.share({ text: bulkKakaoMessage });
                                                    setShowBulkKakaoModal(false);
                                                    setBulkKakaoMessage('');
                                                    setBulkKakaoImage(null);
                                                    setBulkKakaoImageFile(null);
                                                    setBulkKakaoType('text');
                                                    return;
                                                } catch (error) {
                                                    // 취소/오류 시 폴백으로 진행
                                                }
                                            }

                                            // 폴백: 클립보드에 복사 후 카카오톡 열기
                                            try {
                                                await navigator.clipboard.writeText(bulkKakaoMessage);
                                            } catch (error) {
                                                const textArea = document.createElement('textarea');
                                                textArea.value = bulkKakaoMessage;
                                                document.body.appendChild(textArea);
                                                textArea.select();
                                                document.execCommand('copy');
                                                document.body.removeChild(textArea);
                                            }

                                            // 카카오톡 앱 열기
                                            window.location.href = 'kakaotalk://';
                                            alert('메시지를 복사했습니다. 카카오톡이 열리면 붙여넣기 하세요.');
                                        } else {
                                            // 이미지의 경우 Web Share API 시도
                                            if (navigator.share && bulkKakaoImageFile) {
                                                try {
                                                    await navigator.share({ files: [bulkKakaoImageFile] });
                                                    setShowBulkKakaoModal(false);
                                                    setBulkKakaoMessage('');
                                                    setBulkKakaoImage(null);
                                                    setBulkKakaoImageFile(null);
                                                    setBulkKakaoType('text');
                                                    return;
                                                } catch (error) {
                                                    // 취소/오류 시 폴백으로 진행
                                                }
                                            }

                                            // 폴백: 카카오톡 앱 바로 열기
                                            window.location.href = 'kakaotalk://';
                                            alert('카카오톡이 열렸습니다. 이미지를 직접 첨부해주세요.');
                                        }
                                    } catch (error) {
                                        console.error('카카오톡 열기 실패:', error);
                                        alert('카카오톡 앱을 찾을 수 없습니다. 직접 카카오톡을 열어주세요.');
                                    }

                                    setShowBulkKakaoModal(false);
                                    setBulkKakaoMessage('');
                                    setBulkKakaoImage(null);
                                    setBulkKakaoImageFile(null);
                                    setBulkKakaoType('text');
                                }}
                                className="flex-1 py-3 px-4 bg-yellow-500 text-white rounded-xl font-medium hover:bg-yellow-600 transition-colors"
                            >
                                전송
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 이스터에그: 도파민 부스터 */}
            <EuphoriaModal isOpen={showEaster} onClose={() => setShowEaster(false)} />
        </div>
    );
};

export default CustomerManagement;